/**
 * Shell do painel: navegação, identificação do usuário e saída.
 *
 * Os itens de menu são filtrados por permissão pelo componente `Se`, que lê a mesma matriz do
 * domínio usada pela API. Esconder o item é conveniência; a recusa que vale é a do servidor.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router';

import { useAutenticacao } from '../autenticacao/contexto.tsx';
import { Se } from '../autenticacao/Guardas.tsx';
import { meuPerfil } from '../api/cliente.ts';
import type { Acao } from '@emaster/dominio';

const ITENS: readonly {
  readonly para: string;
  readonly rotulo: string;
  readonly acao: Acao;
  readonly icone: ReactNode;
}[] = [
  {
    para: '/dashboard',
    rotulo: 'Dashboard',
    acao: 'ver_proprias',
    icone: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
        />
      </svg>
    ),
  },
  {
    para: '/propostas',
    rotulo: 'Propostas',
    acao: 'ver_proprias',
    icone: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
        />
      </svg>
    ),
  },
  {
    para: '/nova',
    rotulo: 'Nova proposta',
    acao: 'criar',
    icone: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    ),
  },
  {
    para: '/usuarios',
    rotulo: 'Usuários e Permissões',
    acao: 'gerenciar_usuarios',
    icone: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
        />
      </svg>
    ),
  },
  {
    para: '/relatorios',
    rotulo: 'Relatórios',
    acao: 'ver_auditoria',
    icone: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
        />
      </svg>
    ),
  },
  {
    para: '/meu-perfil',
    rotulo: 'Meu Perfil',
    acao: 'ver_proprias',
    icone: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        className="h-4 w-4"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
        />
      </svg>
    ),
  },
];

/** Rótulo legível do perfil, para exibição. */
const NOME_PERFIL: Readonly<Record<string, string>> = {
  admin: 'Administrador',
  diretor: 'Diretor',
  vendedor: 'Vendedor',
  leitor: 'Leitor',
};

export function Shell() {
  const { sessao, sair } = useAutenticacao();

  const [tema, setTema] = useState<'claro' | 'escuro'>(
    () => (localStorage.getItem('emaster-tema') as 'claro' | 'escuro' | null) ?? 'claro',
  );

  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [iniciais, setIniciais] = useState('');

  useEffect(() => {
    if (sessao === null) return;
    void meuPerfil().then((r) => {
      if (r.ok) {
        setFotoUrl(r.dados.fotoUrl);
        const partes = [r.dados.nome, r.dados.sobrenome].filter((p) => p !== null && p !== '');
        if (partes.length > 0) {
          setIniciais(partes.map((p) => (p ?? '').charAt(0).toUpperCase()).join(''));
        } else {
          setIniciais(
            (sessao.email.split('@')[0] ?? '')
              .split('.')
              .map((p) => p.charAt(0).toUpperCase())
              .join('')
              .slice(0, 2),
          );
        }
      }
    });
  }, [sessao]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', tema === 'escuro');
    localStorage.setItem('emaster-tema', tema);
  }, [tema]);

  return (
    <div className="min-h-screen dark:bg-gray-900">
      <header className="sticky top-0 z-40 border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur">
        <nav className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-2 sm:h-16 sm:px-4 lg:px-6">
          <div className="flex items-center gap-1 sm:gap-3 md:gap-6">
            <img
              src="https://committed.proposta.emaster.info/images/emaster-logo1.png"
              alt="eMaster Cloud"
              className="hidden h-9 w-auto sm:block"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            {/*
             * Menu responsivo: sempre visível.
             *
             * - Mobile (< sm): ícones compactos, sem gap, padding mínimo.
             * - Tablet (sm–md): ícones com um pouco mais de espaço.
             * - Desktop (≥ md): ícone + rótulo de texto.
             *
             * Não usa hamburger/drawer: esconder a navegação atrás de um clique extra é a causa do
             * relato "não acho onde gerar proposta". Os ícones são reconhecíveis o bastante para
             * dispensar o texto em tela estreita.
             */}
            <div className="flex items-center gap-0 sm:gap-1 md:gap-4">
              {ITENS.map((i) => (
                <Se key={i.para} acao={i.acao}>
                  <NavLink
                    to={i.para}
                    title={i.rotulo}
                    className={({ isActive }) =>
                      [
                        'flex items-center justify-center gap-1 px-1.5 py-2 text-sm font-semibold transition-all sm:px-2 sm:py-3 md:gap-2 md:px-3',
                        isActive
                          ? 'border-b-2 border-brand-accent text-brand-accent rounded-t-lg'
                          : 'border-b-2 border-transparent text-gray-500 dark:text-gray-400 hover:text-brand-blue dark:hover:text-white rounded-t-lg',
                      ].join(' ')
                    }
                  >
                    {i.icone}
                    <span className="hidden md:inline">{i.rotulo}</span>
                  </NavLink>
                </Se>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3">
            <div className="hidden text-right md:block">
              <p className="text-sm font-medium text-brand-blue dark:text-white">{sessao?.email}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {sessao?.perfil === null || sessao?.perfil === undefined
                  ? 'Sem perfil'
                  : (NOME_PERFIL[sessao.perfil] ?? sessao.perfil)}
              </p>
            </div>

            {/* Botão tema claro/escuro */}
            <button
              type="button"
              aria-label={tema === 'claro' ? 'Ativar tema escuro' : 'Ativar tema claro'}
              onClick={() => {
                setTema((t) => (t === 'claro' ? 'escuro' : 'claro'));
              }}
              className="rounded-lg p-1.5 text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {tema === 'claro' ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
                  />
                </svg>
              )}
            </button>

            {/* Foto do vendedor */}
            {sessao !== null &&
              (fotoUrl !== null ? (
                <img
                  src={fotoUrl}
                  alt={sessao.email}
                  className="h-7 w-7 rounded-full object-cover border-2 border-brand-accent sm:h-9 sm:w-9"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-brand-accent bg-brand-accent text-xs font-bold text-white sm:h-9 sm:w-9 sm:text-sm">
                  {iniciais}
                </span>
              ))}

            <button
              type="button"
              onClick={() => {
                void sair();
              }}
              aria-label="Sair"
              className="rounded-lg px-1.5 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-brand-blue dark:hover:text-white sm:px-3 sm:py-1.5 sm:text-sm"
            >
              Sair
            </button>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-[1600px] px-2 py-4 sm:px-4 sm:py-6 lg:px-6">
        <Outlet />
      </main>
    </div>
  );
}
